import { createRng } from './rng';
import { buildOrganization } from './organization';
import { buildPeople } from './people';
import { buildProducts } from './products';
import { buildProcesses } from './processes';
import { DEFAULT_APPLICATION_COUNT, buildApplications } from './applications';
import { buildTeams } from './teams';
import { buildRelationships } from './relationships';
import { buildDelivery } from './delivery';
import { buildSourcing } from './sourcing';
import { buildFinance } from './finance';
import { buildGovernance } from './governance';
import { buildScenarios } from './scenarios';
import type { AuroraDataset } from './types';

/**
 * `buildAuroraDataset(42)` twice must give byte-identical output. Everything in
 * this package exists to make that sentence true: one seed, one fixed `asOf`,
 * named child streams per generator, and no clock.
 */

export const DEFAULT_SEED = 42;

/** The dataset's "today". Fixed so that every relative date is reproducible. */
export const AS_OF = '2026-05-20';

export interface BuildOptions {
  asOf?: string;
  applicationCount?: number;
}

export function buildAuroraDataset(
  seed: number = DEFAULT_SEED,
  options: BuildOptions = {},
): AuroraDataset {
  const asOf = options.asOf ?? AS_OF;
  const applicationCount = options.applicationCount ?? DEFAULT_APPLICATION_COUNT;
  const rng = createRng(seed);
  const fiscalYear = Number.parseInt(asOf.slice(0, 4), 10);

  const { enterprise, departments, valueStreams, productGroups } = buildOrganization(asOf, rng);
  const { personas, people } = buildPeople(asOf, rng);
  const products = buildProducts(asOf, rng, valueStreams);
  const processes = buildProcesses(asOf, rng);
  const applications = buildApplications(asOf, rng, applicationCount);
  const teams = buildTeams(asOf, rng, products, valueStreams);

  const relationships = buildRelationships({
    asOf,
    rng,
    products,
    applications,
    processes,
    teams,
  });

  const delivery = buildDelivery(asOf, rng, products, applications);
  const sourcing = buildSourcing(asOf, rng, applications, products);

  const finance = buildFinance({
    asOf,
    fiscalYear,
    rng,
    products,
    applications,
    teams,
    contracts: sourcing.contracts,
    statementsOfWork: sourcing.statementsOfWork,
    productApplicationLinks: relationships.productApplicationLinks,
    teamProductLinks: relationships.teamProductLinks,
  });

  const governance = buildGovernance({ asOf, rng, products, applications, processes });

  const scenarios = buildScenarios({
    asOf,
    rng,
    products,
    applications,
    teams,
    costActuals: finance.costActuals,
  });

  return {
    seed,
    asOf,
    fiscalYear,
    periodStart: `${fiscalYear}-01-01`,
    periodEnd: asOf,
    currency: 'USD',

    enterprise,
    departments,
    valueStreams,
    productGroups,
    products,
    processes,
    applications,

    personas,
    people,
    teams,

    workItems: delivery.workItems,
    releases: delivery.releases,
    deployments: delivery.deployments,
    flowMetrics: delivery.flowMetrics,
    incidents: delivery.incidents,

    fundingLines: finance.fundingLines,
    costActuals: finance.costActuals,
    costAllocations: finance.costAllocations,
    benefits: finance.benefits,

    suppliers: sourcing.suppliers,
    contracts: sourcing.contracts,
    statementsOfWork: sourcing.statementsOfWork,
    obligations: sourcing.obligations,

    productProcessLinks: relationships.productProcessLinks,
    productApplicationLinks: relationships.productApplicationLinks,
    applicationProcessLinks: relationships.applicationProcessLinks,
    teamProductLinks: relationships.teamProductLinks,
    productDependencyLinks: relationships.productDependencyLinks,

    evidence: governance.evidence,
    assessments: governance.assessments,
    decisions: governance.decisions,
    approvals: governance.approvals,
    auditEvents: governance.auditEvents,
    dataQualityRules: governance.dataQualityRules,
    dataQualityIssues: governance.dataQualityIssues,
    sourceConflicts: governance.sourceConflicts,
    mappingSets: governance.mappingSets,

    connectors: governance.connectors,
    ingestionRuns: governance.ingestionRuns,
    lineageNodes: governance.lineageNodes,
    lineageEdges: governance.lineageEdges,
    lineageMappings: governance.lineageMappings,

    scenarios,
  };
}

/** Entity counts, useful for smoke tests and for the synthetic-data banner. */
export function datasetCounts(dataset: AuroraDataset): Record<string, number> {
  return {
    departments: dataset.departments.length,
    valueStreams: dataset.valueStreams.length,
    productGroups: dataset.productGroups.length,
    products: dataset.products.length,
    activeProducts: dataset.products.filter((p) => p.lifecycle === 'Active').length,
    processes: dataset.processes.length,
    applications: dataset.applications.length,
    people: dataset.people.length,
    personas: dataset.personas.length,
    teams: dataset.teams.length,
    workItems: dataset.workItems.length,
    releases: dataset.releases.length,
    deployments: dataset.deployments.length,
    incidents: dataset.incidents.length,
    fundingLines: dataset.fundingLines.length,
    costActuals: dataset.costActuals.length,
    costAllocations: dataset.costAllocations.length,
    benefits: dataset.benefits.length,
    suppliers: dataset.suppliers.length,
    contracts: dataset.contracts.length,
    statementsOfWork: dataset.statementsOfWork.length,
    obligations: dataset.obligations.length,
    productProcessLinks: dataset.productProcessLinks.length,
    productApplicationLinks: dataset.productApplicationLinks.length,
    applicationProcessLinks: dataset.applicationProcessLinks.length,
    teamProductLinks: dataset.teamProductLinks.length,
    evidence: dataset.evidence.length,
    assessments: dataset.assessments.length,
    decisions: dataset.decisions.length,
    auditEvents: dataset.auditEvents.length,
    dataQualityIssues: dataset.dataQualityIssues.length,
    sourceConflicts: dataset.sourceConflicts.length,
    connectors: dataset.connectors.length,
    ingestionRuns: dataset.ingestionRuns.length,
    scenarios: dataset.scenarios.length,
  };
}

export * from './types';
export { createRng } from './rng';
export { AURORA_NAMESPACE, correlationIdFor, idFor, uuidV5 } from './ids';
export { SOURCE_SYSTEMS, bandFor, makeProvenance } from './provenance';
export { ENTERPRISE_CODE, TENANT_ID } from './organization';
export { PERSONAS } from './people';
export { CURATED_PRODUCT_CODES, NORTH_STAR_PRODUCT_CODE, healthFromScore } from './products';
export { DEFAULT_APPLICATION_COUNT, technicalHealthFromScore } from './applications';
export { contractorMix } from './teams';
export { YTD_FRACTION } from './finance';
export { CREW_MANAGED_SERVICE_CONTRACT, totalContractedValue } from './sourcing';
export { ASSESSMENT_CRITERIA, ASSESSMENT_MODEL_VERSION, weightedScoreOutOf100 } from './governance';
export type { CriterionDefinition } from './governance';
export { SCENARIO_ASSUMPTIONS, SCENARIO_FORMULA_VERSION } from './scenarios';
export {
  addDays,
  at,
  daysBetween,
  clamp,
  dollars,
  groupBy,
  indexBy,
  millions,
  money,
  ratio,
  round,
  splitCents,
  sumCents,
  toIsoDate,
  toTimestamp,
  trailingMonths,
  unique,
} from './support';
